<template>
    <div class="step" :class="{ 'is-embedded': embedded }">
        <div class="column">
            <StepHeader v-if="embedded" embedded :title="t('onboarding.calendarFirst.assessmentStep.title')" :lede="t('onboarding.calendarFirst.assessmentStep.lede')" />
            <BasePageHeader
                v-else
                :title="t('onboarding.calendarFirst.assessmentStep.heading')"
                :subtitle="t('onboarding.calendarFirst.assessmentStep.lede')"
                no-border
                subtitle-below>
                <template #eyebrow
                    ><p>{{ t("onboarding.calendarFirst.assessmentStep.title") }}</p></template
                >
            </BasePageHeader>

            <!-- The questions arrive after the step opens, and the session ID with them; an empty survey would flash
                 "0 / 0" and a submit button. An empty bank still renders the survey, so the linear flow keeps its skip. -->
            <div v-if="assessment.session_id === ''" class="survey survey-loading" aria-hidden="true">
                <div v-for="row in 3" :key="row" class="survey-loading-question">
                    <SkeletonLoader class="h-4 w-3/4 rounded motion-reduce:animate-none" />
                    <div class="flex justify-between gap-2">
                        <SkeletonLoader v-for="option in 7" :key="option" class="size-8 rounded-full motion-reduce:animate-none" />
                    </div>
                </div>
            </div>
            <component
                :is="surveyComponent"
                v-else
                ref="survey"
                class="survey"
                :class="{ 'is-legacy': !sliderEnabled }"
                layout="embedded"
                :questions="assessment.questions"
                :saved-responses="assessment.saved_responses"
                :session-id="assessment.session_id"
                :submit-label="t('onboarding.calendarFirst.assessmentStep.submit')"
                @submitted="emit('submitted')">
                <!-- Embedded setup already provides an exit through its persistent rail; the linear flow
                     needs this skip control to proceed without answering.
                     -->
                <template v-if="skippable ?? !embedded" #escape>
                    <BaseButton theme="tertiary" class="step-skip" :disabled="busy" @click="emit('skip')">{{ t("onboarding.calendarFirst.actions.skipForNow") }}</BaseButton>
                </template>
            </component>
        </div>
    </div>
</template>

<script setup lang="ts">
import { fetchApi } from "~vue/api";
import StepHeader from "~vue/CalendarFirstOnboarding/StepHeader.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import type PerspectiveSurvey from "~vue/Perspective/PerspectiveSurvey.vue";
import { usePerspectiveSurveyComponent } from "~vue/Perspective/usePerspectiveSurveyComponent";
import { onboardingDevToolsStore, setAssessmentFillAvailable } from "~vue/stores/onboardingDevToolsStore";
import { computed, onBeforeUnmount, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { AssessmentState } from "./types";

const { embedded = false, skippable = undefined, assessment, busy } = defineProps<{ embedded?: boolean; skippable?: boolean; assessment: AssessmentState; busy: boolean }>();

const emit = defineEmits<{ submitted: []; skip: [] }>();
const { t } = useI18n();

const { sliderEnabled, surveyComponent } = usePerspectiveSurveyComponent();
const survey = useTemplateRef<InstanceType<typeof PerspectiveSurvey>>("survey");

/*
 * Staff testing bypasses sixty manual answers through the Perspectives demo endpoint, which persists them
 * before submission.
 */
watch(
    () => onboardingDevToolsStore.fillAssessmentToken,
    async (token) => {
        if (token === 0) {
            return;
        }
        const response = await fetchApi(`/v1/perspectives/sessions/${assessment.session_id}/fill-remaining`, { method: "POST" });
        if (!response.ok) {
            return;
        }
        const payload = (await response.json()) as { filled_responses?: Record<string, number> };
        survey.value?.adoptResponses(payload.filled_responses ?? {});
    },
);

const fillAvailable = computed(() => assessment.questions.length > 0);
watch(fillAvailable, (available) => setAssessmentFillAvailable(available), { immediate: true });

onBeforeUnmount(() => setAssessmentFillAvailable(false));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .step {
        @apply px-6 py-6 md:py-10;
    }
    .step.is-embedded {
        @apply p-0;
    }

    .column {
        @apply mx-auto w-full max-w-168;
    }
    .step.is-embedded .column {
        @apply mx-0 max-w-none;
    }

    /* The redesigned survey's sticky progress header carries its own top padding; only the original needs this gap. */
    .survey.is-legacy {
        @apply mt-6;
    }

    .survey-loading {
        @apply mt-6 flex flex-col gap-10 py-10;
    }

    .survey-loading-question {
        @apply flex flex-col gap-5;
    }

    /* The host already sets the page gutter, and the survey's own would indent it
       against the heading directly above it. */
    .survey :deep(.progress),
    .survey :deep(.footer) {
        @apply px-0;
    }
    .survey :deep(.scroll-container) > * {
        @apply px-0;
    }
}
</style>
